import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Download, DollarSign, Loader2, ChevronDown, ChevronRight,
  Users, Clock, Building2, Calendar
} from 'lucide-react';
import { toast } from 'sonner';
import { format, subDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth, subMonths } from 'date-fns';
import { fmtTz } from "../lib/time";

// Pay-period helpers
function getLastBiWeek(today = new Date()) {
  // Pay period ends last Sunday, starts 14 days before
  const end = startOfWeek(today, { weekStartsOn: 1 }); // this Monday
  const periodEnd = subDays(end, 1); // last Sunday
  const periodStart = subDays(periodEnd, 13);
  return { start: periodStart, end: periodEnd };
}
function getLastSemiMonthly(today = new Date()) {
  const day = today.getDate();
  if (day <= 15) {
    // last full second-half period (16th - end of last month)
    const lastMonth = subMonths(today, 1);
    const start = new Date(lastMonth.getFullYear(), lastMonth.getMonth(), 16);
    const end = endOfMonth(lastMonth);
    return { start, end };
  }
  // last 1st-15th period (current month)
  const start = new Date(today.getFullYear(), today.getMonth(), 1);
  const end = new Date(today.getFullYear(), today.getMonth(), 15);
  return { start, end };
}
function getLastMonth(today = new Date()) {
  const last = subMonths(today, 1);
  return { start: startOfMonth(last), end: endOfMonth(last) };
}

export default function AdminPayroll() {
  const { authAxios } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(format(getLastBiWeek().start, 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(getLastBiWeek().end, 'yyyy-MM-dd'));
  const [selectedEmployee, setSelectedEmployee] = useState('all');
  const [expanded, setExpanded] = useState({});

  const fetchEmployees = useCallback(async () => {
    try {
      const res = await authAxios.get('/employees');
      setEmployees(res.data.filter((e) => e.role === 'employee'));
    } catch (err) {
      console.error('Failed to fetch employees', err);
    }
  }, [authAxios]);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const params = { start_date: startDate, end_date: endDate };
      if (selectedEmployee !== 'all') params.employee_id = selectedEmployee;
      const res = await authAxios.get('/admin/reports/summary', { params });
      setReportData(res.data);
    } catch (err) {
      toast.error('Failed to load payroll report');
    } finally {
      setLoading(false);
    }
  }, [authAxios, startDate, endDate, selectedEmployee]);

  useEffect(() => { fetchEmployees(); }, [fetchEmployees]);
  useEffect(() => { fetchReport(); }, [fetchReport]);

  const handleExportPayroll = async () => {
    try {
      const params = new URLSearchParams({ start_date: startDate, end_date: endDate });
      const response = await authAxios.get(`/admin/reports/payroll-csv?${params.toString()}`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `payroll_${startDate}_to_${endDate}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Payroll CSV downloaded');
    } catch (err) {
      toast.error('Failed to export CSV');
    }
  };

  const handleExportDetailed = async () => {
    try {
      const params = new URLSearchParams({ start_date: startDate, end_date: endDate });
      if (selectedEmployee !== 'all') params.append('employee_id', selectedEmployee);
      const response = await authAxios.get(`/admin/reports/export-csv?${params.toString()}`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `time_entries_${startDate}_to_${endDate}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Time entries CSV downloaded');
    } catch (err) {
      toast.error('Failed to export CSV');
    }
  };

  const formatHrs = (mins) => `${Math.floor(mins / 60)}h ${mins % 60}m`;

  const setPreset = (preset) => {
    const today = new Date();
    let range;
    switch (preset) {
      case 'last_biweek': range = getLastBiWeek(today); break;
      case 'last_semi': range = getLastSemiMonthly(today); break;
      case 'last_month': range = getLastMonth(today); break;
      case 'this_week': range = { start: startOfWeek(today, { weekStartsOn: 1 }), end: endOfWeek(today, { weekStartsOn: 1 }) }; break;
      case 'this_month': range = { start: startOfMonth(today), end: endOfMonth(today) }; break;
      case 'last_30': range = { start: subDays(today, 30), end: today }; break;
      default: return;
    }
    setStartDate(format(range.start, 'yyyy-MM-dd'));
    setEndDate(format(range.end, 'yyyy-MM-dd'));
  };

  const summaries = reportData?.summaries || [];
  const grandCost = reportData?.grand_total_cost ?? 0;
  const grandHours = reportData?.grand_total_hours ?? 0;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-payroll-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-stone-900 font-heading">Payroll &amp; Records</h1>
          <p className="text-stone-500 mt-1">Pay-period summary by employee with per-project breakdown.</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleExportDetailed}
            data-testid="export-entries-csv-btn"
          >
            <Download className="w-4 h-4 mr-2" /> Time Entries CSV
          </Button>
          <Button
            onClick={handleExportPayroll}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="export-payroll-csv-btn"
          >
            <DollarSign className="w-4 h-4 mr-2" /> Payroll CSV
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-4">
            <div>
              <Label>Pay Period Start</Label>
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} data-testid="payroll-start-date" />
            </div>
            <div>
              <Label>Pay Period End</Label>
              <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} data-testid="payroll-end-date" />
            </div>
            <div>
              <Label>Employee</Label>
              <Select value={selectedEmployee} onValueChange={setSelectedEmployee}>
                <SelectTrigger data-testid="payroll-employee-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Employees</SelectItem>
                  {employees.map((e) => (
                    <SelectItem key={e.id} value={e.id}>{e.full_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button onClick={fetchReport} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="payroll-refresh-btn">
                <Calendar className="w-4 h-4 mr-2" /> Refresh
              </Button>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 pt-2 border-t">
            <span className="text-xs text-stone-500 self-center mr-2">Quick presets:</span>
            <Button size="sm" variant="outline" onClick={() => setPreset('last_biweek')} data-testid="preset-last-biweek">Last Bi-weekly</Button>
            <Button size="sm" variant="outline" onClick={() => setPreset('last_semi')} data-testid="preset-last-semi">Last Semi-Monthly</Button>
            <Button size="sm" variant="outline" onClick={() => setPreset('last_month')} data-testid="preset-last-month">Last Month</Button>
            <Button size="sm" variant="outline" onClick={() => setPreset('this_week')}>This Week</Button>
            <Button size="sm" variant="outline" onClick={() => setPreset('this_month')}>This Month</Button>
            <Button size="sm" variant="outline" onClick={() => setPreset('last_30')}>Last 30 Days</Button>
          </div>
        </CardContent>
      </Card>

      {/* Summary stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <Card className="bg-emerald-50 border-emerald-200">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="w-12 h-12 bg-emerald-600 rounded-xl flex items-center justify-center"><Users className="w-6 h-6 text-white" /></div>
            <div>
              <p className="text-xs text-stone-500 uppercase">Employees</p>
              <p className="text-2xl font-bold text-stone-900">{summaries.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="w-12 h-12 bg-amber-600 rounded-xl flex items-center justify-center"><Clock className="w-6 h-6 text-white" /></div>
            <div>
              <p className="text-xs text-stone-500 uppercase">Total Hours</p>
              <p className="text-2xl font-bold text-stone-900" data-testid="payroll-total-hours">{grandHours.toFixed(2)}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center"><DollarSign className="w-6 h-6 text-white" /></div>
            <div>
              <p className="text-xs text-stone-500 uppercase">Total Labour Cost</p>
              <p className="text-2xl font-bold text-stone-900" data-testid="payroll-total-cost">${grandCost.toFixed(2)}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Summary table */}
      <Card>
        <CardHeader>
          <CardTitle>Per-Employee Summary ({fmtTz(startDate, 'MMM d')} – {fmtTz(endDate, 'MMM d, yyyy')})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div>
          ) : summaries.length === 0 ? (
            <div className="text-center py-12 text-stone-500">No time entries in this pay period.</div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8"></TableHead>
                    <TableHead>Employee</TableHead>
                    <TableHead>Department</TableHead>
                    <TableHead className="text-right">Days</TableHead>
                    <TableHead className="text-right">Hours</TableHead>
                    <TableHead className="text-right">Rate/hr</TableHead>
                    <TableHead className="text-right">Labour Cost</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {summaries.map((s) => {
                    const isOpen = !!expanded[s.user_id];
                    return (
                      <React.Fragment key={s.user_id}>
                        <TableRow
                          className="cursor-pointer hover:bg-stone-50"
                          onClick={() => setExpanded((e) => ({ ...e, [s.user_id]: !isOpen }))}
                          data-testid={`payroll-row-${s.user_id}`}
                        >
                          <TableCell>{isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}</TableCell>
                          <TableCell className="font-medium">{s.user_name}</TableCell>
                          <TableCell className="text-stone-600">{s.department || '-'}</TableCell>
                          <TableCell className="text-right font-mono">{s.days_worked}</TableCell>
                          <TableCell className="text-right font-mono text-emerald-700">{s.total_work_hours.toFixed(2)}</TableCell>
                          <TableCell className="text-right font-mono text-stone-600">${(s.hourly_rate || 0).toFixed(2)}</TableCell>
                          <TableCell className="text-right font-mono font-bold text-blue-700">${s.total_cost.toFixed(2)}</TableCell>
                        </TableRow>
                        {isOpen && (
                          <TableRow className="bg-stone-50/60">
                            <TableCell></TableCell>
                            <TableCell colSpan={6} className="py-3">
                              <div className="text-xs uppercase tracking-wider text-stone-500 mb-2 flex items-center gap-1">
                                <Building2 className="w-3.5 h-3.5" /> Project Breakdown
                              </div>
                              <table className="w-full text-sm" data-testid={`payroll-projects-${s.user_id}`}>
                                <thead>
                                  <tr className="text-left text-stone-500">
                                    <th className="py-1 pr-4">Project</th>
                                    <th className="py-1 pr-4 text-right">Days</th>
                                    <th className="py-1 pr-4 text-right">Hours</th>
                                    <th className="py-1 pr-4 text-right">Cost</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {s.projects.map((p, idx) => (
                                    <tr key={`${p.project_id || 'none'}-${idx}`} className="border-t border-stone-200">
                                      <td className="py-1.5 pr-4">
                                        <Badge variant="outline" className={p.project_id ? 'border-emerald-300 text-emerald-800' : 'border-stone-300 text-stone-600'}>
                                          {p.project_name}
                                        </Badge>
                                      </td>
                                      <td className="py-1.5 pr-4 text-right font-mono">{p.days}</td>
                                      <td className="py-1.5 pr-4 text-right font-mono text-emerald-700">{p.work_hours.toFixed(2)}</td>
                                      <td className="py-1.5 pr-4 text-right font-mono font-medium">${p.cost.toFixed(2)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
